import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { BASE_URL } from '../../config';
import authStorage from '../../utils/authStorage';

const BRAND = '#E53E14';

/*
 * The server's own limits (api helpers/corporateBilling.js), used when the plan
 * endpoint does not send them. The screen checks the same rules as you type so
 * nobody has to press Save to find a typo; the server still has the last word.
 */
const FALLBACK_LIMITS = {
    maxTiers: 10, maxRate: 10000,
    minLookbackMonths: 1, maxLookbackMonths: 24,
    minFreeOnboardingMonths: 0, maxFreeOnboardingMonths: 12,
};

/*
 * Buttons. Styles are spelled out inline because this project loads only the
 * @tailwind utilities with no preflight, so a bare <button> keeps Chrome's grey
 * default chrome.
 */
const BTN_BASE = {
    display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 8, padding: '7px 14px',
    fontSize: 13, fontWeight: 600, lineHeight: 1.2, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'background-color .15s, border-color .15s, color .15s',
};
const BTN = {
    // Quiet secondary action: white with a grey outline.
    outline: { ...BTN_BASE, background: '#fff', border: '1px solid #d1d5db', color: '#374151' },
    // Adds something: dashed brand outline, so it reads as "a new row goes here".
    add: { ...BTN_BASE, background: '#FFF5F3', border: `1.5px dashed ${BRAND}`, color: BRAND },
};
const hoverBg = (on, off) => ({
    onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = on; },
    onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = off; },
});

const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
);
const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
);
const HistoryIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
const BinIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></svg>
);

/** A field's red message, under it. */
const FieldError = ({ children }) =>
    children ? <div className="mt-1 text-xs" role="alert" style={{ color: '#b91c1c', maxWidth: 260 }}>{children}</div> : null;
/** A warning that does not block saving. */
const FieldWarn = ({ children }) =>
    children ? <div className="mt-1 text-xs" style={{ color: '#b45309', maxWidth: 360 }}>{children}</div> : null;
const badBorder = (m) => (m ? { borderColor: '#dc2626', outlineColor: '#dc2626' } : undefined);

/** `YYYY-MM-DD` of today, local. */
const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Corporate billing configuration.
 *
 * ⚠️ THIS PRICES COMPANIES, NOT MEMBERS. Individual members pay through Stripe
 * subscriptions and nothing on this screen touches that. Nothing here charges
 * anybody either — the module calculates and collection happens offline.
 *
 * TWO THINGS THIS SCREEN HAS TO COMMUNICATE, or it will be misread:
 *   1. Saving PUBLISHES A VERSION. It does not edit the current one, and
 *      invoices already finalised keep the rates they were calculated with.
 *   2. Pricing is PROGRESSIVE. Each band charges its own rate — a company in
 *      band 3 is not charged the band-3 rate on its whole head count. The live
 *      preview exists mainly to make that visible before anyone saves.
 *
 * The preview is priced BY THE SERVER (`/billing/preview`) rather than by a
 * copy of the formula here. A second implementation of the tier arithmetic in
 * the browser would be a second thing to keep in step with the client's
 * spreadsheet, and the first time they disagreed it would be on an invoice.
 */

/** The head counts the client published in Billing.xlsx, used as the preview row. */
const PREVIEW_POINTS = [999, 10000, 12000, 100000, 150000];

const money = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const shortDate = (d) => {
    if (!d) return '—';
    /*
     * A bare `YYYY-MM-DD` is parsed as UTC midnight, which renders as the DAY
     * BEFORE for anyone west of Greenwich — so an effective date of 1 Nov would
     * be shown as 31 Oct, in a screen whose whole point is which month a price
     * applies to. Reading it as local midnight keeps the date the admin typed.
     */
    const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** First of next month, in the `YYYY-MM-DD` a date input wants. */
const firstOfNextMonth = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
        .toISOString()
        .slice(0, 10);
};

const CorporateBilling = ({ onFlash }) => {
    const token = authStorage.getItem('auth-token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [meta, setMeta] = useState(null);

    // The table being edited. Held as STRINGS so a half-typed number is never
    // mistaken for a real one, and so clearing a box does not read as zero.
    const [rows, setRows] = useState([]);
    // Which tier field was typed in last: a band boundary is shown on both "up to"
    // and the next band's "users from", and its error belongs beside the one edited.
    const [lastEdit, setLastEdit] = useState(null); // { i, field }
    const [mode, setMode] = useState('accepted');
    const [lookback, setLookback] = useState('3');
    const [freeMonths, setFreeMonths] = useState('1');
    const [effectiveFrom, setEffectiveFrom] = useState(firstOfNextMonth());
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const [preview, setPreview] = useState([]);
    const [previewError, setPreviewError] = useState('');

    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const [companies, setCompanies] = useState(null);
    const [companiesLoading, setCompaniesLoading] = useState(false);

    /*
     * Per-company terms editing. `editing` is the company id whose row is open;
     * one at a time, because two half-finished edits on screen is how the wrong
     * one gets saved.
     */
    const [editing, setEditing] = useState(null);
    const [editDraft, setEditDraft] = useState({ connectionDate: '', freeOnboardingMonths: '', note: '' });
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    // Derived values go BELOW the state they read — a const referenced above its
    // own declaration is a temporal-dead-zone error that blanks the whole page.
    const current = meta?.current;
    // A version published with a future date is not in force yet. Without this the
    // screen would keep reporting the old rates after a successful publish and
    // look as if the save had been dropped.
    const scheduled = meta?.scheduled;
    const modes = meta?.billableModes || [];
    const limits = { ...FALLBACK_LIMITS, ...(meta?.limits || {}) };

    /*
     * What a save is measured against: the version already scheduled if there is
     * one, otherwise the version in force. Editing this morning's change should
     * show as clean until it is edited again, not as permanently unsaved against
     * the rates it will replace.
     */
    const baseline = scheduled || current;

    /*
     * ⚠️ EACH CARD SAVES, BUT A SAVE ALWAYS PUBLISHES THE WHOLE CONFIGURATION.
     *
     * Rates, the billable population and the activity window all change the
     * amount, so they are versioned together — an invoice that stamped only some
     * of them could not be reproduced through the parts left out. The two cards
     * therefore track their own unsaved state separately, so each can say whether
     * IT has been touched, but either Save button writes one version containing
     * both. After it lands, both cards are clean.
     */
    const numEq = (a, b) => {
        const x = a === '' || a === null || a === undefined ? null : Number(a);
        const y = b === '' || b === null || b === undefined ? null : Number(b);
        return x === y;
    };

    const tiersDirty = (() => {
        const base = baseline?.tiers || [];
        if (!baseline || base.length !== rows.length) return rows.length > 0 && Boolean(baseline);
        return !base.every(
            (t, i) => numEq(t.upTo, rows[i].upTo) && numEq(t.rate, rows[i].rate)
        );
    })();

    const audienceDirty = Boolean(baseline) && (
        mode !== baseline.billableMode ||
        !numEq(lookback, baseline.inactiveLookbackMonths) ||
        !numEq(freeMonths, baseline.freeOnboardingMonths)
    );

    const headers = { 'x-access-token': token };

    /* ------------------------------------------------------------------ *
     * Load
     * ------------------------------------------------------------------ */
    const seedFrom = (plan) => {
        setRows(
            (plan.tiers || []).map((t) => ({
                upTo: t.upTo === null || t.upTo === undefined ? '' : String(t.upTo),
                rate: String(t.rate),
            }))
        );
        setMode(plan.billableMode || 'accepted');
        setLookback(String(plan.inactiveLookbackMonths || 3));
        // Explicit nullish check, never `|| 1`: a deliberate 0 ("bill the
        // connection month") must survive a page load.
        setFreeMonths(
            String(plan.freeOnboardingMonths === undefined || plan.freeOnboardingMonths === null
                ? 1
                : plan.freeOnboardingMonths)
        );
    };

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${BASE_URL}/billing/plan`, { headers });
            const data = res?.data?.data;
            setMeta(data);
            // Seed from the SCHEDULED version when there is one, not the one in
            // force: an admin correcting a change they made this morning should
            // start from what they typed, not from the rates it will replace.
            seedFrom(data.scheduled || data.current);
            setError('');
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not load the billing configuration.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { load(); }, [load]);

    /* ------------------------------------------------------------------ *
     * Field checks — as the admin types, beside the field
     * ------------------------------------------------------------------ *
     * One message per problem, on the field that was being edited: a band's
     * "up to" and the next band's "users from" are the same boundary, so a bad
     * boundary is reported on whichever of the two the admin typed into. The
     * server still validates the whole table (preview and publish); these are
     * so nobody has to wait for that to find a typo.
     */
    const isWhole = (s) => /^\d+$/.test(String(s).trim());
    const fromOf = (list, i) => {
        if (i === 0) return 1;
        const prev = list[i - 1];
        return isWhole(prev.upTo) ? Number(prev.upTo) + 1 : NaN;
    };
    const tierErrors = rows.map((row, i) => {
        const e = {};
        const last = i === rows.length - 1;
        const from = fromOf(rows, i);
        if (!last) {
            let msg = null;
            if (String(row.upTo).trim() === '') msg = 'Enter where this band ends';
            else if (!isWhole(row.upTo) || Number(row.upTo) < 1) msg = 'Use a whole number above 0';
            else if (Number.isFinite(from) && Number(row.upTo) < from) {
                msg = `Must be at least ${from.toLocaleString()} — every band needs at least one user`;
            }
            if (msg) {
                const onNextFrom = lastEdit && lastEdit.field === 'from' && lastEdit.i === i + 1;
                if (onNextFrom) {
                    e.nextFrom = String(row.upTo).trim() === '' ? 'Enter where this band starts'
                        : !isWhole(row.upTo) || Number(row.upTo) < 1 ? 'Use a whole number above 1'
                            : `Must be above ${(from).toLocaleString()} — the band above needs at least one user`;
                } else e.upTo = msg;
            }
        }
        // The server's rule: above zero, no more than MAX_RATE, whole cents.
        const rate = String(row.rate).trim();
        if (rate === '') e.rate = 'Enter the price per user';
        else if (!Number.isFinite(Number(rate))) e.rate = 'Enter a number, e.g. 2.50';
        else if (Number(rate) <= 0) e.rate = 'Must be more than $0 — every user in a band is charged';
        else if (Number(rate) > limits.maxRate) e.rate = `That looks wrong — the most is $${limits.maxRate.toLocaleString()} per user`;
        else if (!/^\d+(\.\d{1,2})?$/.test(rate)) e.rate = 'Use whole cents (at most 2 decimal places)';
        return e;
    });
    // The From field of row i shows the error the row above parked for it.
    const fromError = (i) => (i > 0 ? tierErrors[i - 1].nextFrom : undefined);
    const tierProblem = tierErrors.some((e) => e.upTo || e.rate || e.nextFrom);

    /* The other settings on the page, checked against the same server limits. */
    const wholeIn = (v, min, max, what) => {
        const s = String(v).trim();
        if (s === '') return `Enter ${what}`;
        if (!/^\d+$/.test(s)) return 'Use a whole number of months';
        if (Number(s) < min || Number(s) > max) return `Must be between ${min} and ${max} months`;
        return null;
    };
    const lookbackError = wholeIn(lookback, limits.minLookbackMonths, limits.maxLookbackMonths, 'a number of months');
    const freeMonthsError = wholeIn(freeMonths, limits.minFreeOnboardingMonths, limits.maxFreeOnboardingMonths, 'a number of months (0 for none)');
    const effectiveError = (() => {
        if (!effectiveFrom) return 'Choose the date this version starts';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) || Number.isNaN(new Date(`${effectiveFrom}T00:00:00`).getTime())) return 'Enter a real date';
        const year = Number(effectiveFrom.slice(0, 4));
        const thisYear = new Date().getFullYear();
        if (year < thisYear - 1 || year > thisYear + 5) return `Choose a date between ${thisYear - 1} and ${thisYear + 5}`;
        return null;
    })();
    // Allowed, but worth a second look: a date in the current month re-prices the
    // whole month (invoices use the version in force on the 1st), and a past date
    // re-prices months already lived in.
    const effectiveWarning = (() => {
        if (effectiveError) return null;
        const today = todayIso();
        const monthStart = `${today.slice(0, 7)}-01`;
        if (effectiveFrom < monthStart) return 'This date is in a past month. Months not yet invoiced will be re-priced with these rates.';
        if (effectiveFrom.slice(0, 7) === today.slice(0, 7)) return 'This is inside the current month, so the whole of this month will be priced with these rates.';
        return null;
    })();
    const NOTE_MAX = 200;
    const noteError = note.length > NOTE_MAX ? `Keep the note to ${NOTE_MAX} characters` : null;
    // Anything wrong anywhere stops Save: both Save buttons publish the whole configuration.
    const formProblem = tierProblem || Boolean(lookbackError || freeMonthsError || effectiveError || noteError);

    /* ------------------------------------------------------------------ *
     * Preview — debounced, and priced by the server
     * ------------------------------------------------------------------ */
    const previewTimer = useRef(null);

    useEffect(() => {
        if (!rows.length) return undefined;
        if (previewTimer.current) clearTimeout(previewTimer.current);
        // A field already marked wrong: say so here too (it also keeps Save off)
        // rather than asking the server to price a table we know is broken.
        if (tierProblem) {
            setPreview([]);
            setPreviewError('Fix the highlighted tier fields above to see the prices.');
            return undefined;
        }

        previewTimer.current = setTimeout(async () => {
            const tiers = rows.map((r, i) => ({
                upTo: i === rows.length - 1 && r.upTo === '' ? null : Number(r.upTo),
                rate: Number(r.rate),
            }));
            try {
                const results = await Promise.all(
                    PREVIEW_POINTS.map((users) =>
                        axios.get(`${BASE_URL}/billing/preview`, {
                            headers,
                            params: { users, tiers: JSON.stringify(tiers) },
                        })
                    )
                );
                setPreview(results.map((r, i) => ({ users: PREVIEW_POINTS[i], ...r.data.data })));
                setPreviewError('');
            } catch (err) {
                // The server validates the table before pricing it, so a preview
                // failure IS the validation message — shown here rather than
                // waiting for the admin to press Publish and be refused.
                setPreview([]);
                setPreviewError(err?.response?.data?.message || 'This table cannot be priced.');
            }
        }, 400);

        return () => clearTimeout(previewTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, token]);

    /* ------------------------------------------------------------------ *
     * Edit
     * ------------------------------------------------------------------ */
    const setRow = (index, field, value) =>
        setRows((list) => list.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

    // "USERS FROM" IS EDITABLE, but it is not stored: the bands must meet exactly,
    // with no gap (users nobody bills) and no overlap (users billed twice), so a
    // band's start is always one above the previous band's "up to". Typing a start
    // therefore moves the previous band's "up to" to one below it. The first band
    // always starts at 1.
    const setFrom = (index, value) => {
        setLastEdit({ i: index, field: 'from' });
        const v = String(value).trim();
        setRows((list) => list.map((r, i) => (
            i === index - 1 ? { ...r, upTo: v === '' ? '' : /^\d+$/.test(v) ? String(Number(v) - 1) : v } : r
        )));
    };

    const addRow = () =>
        setRows((list) => {
            // The new band goes in ABOVE the open-ended one: the last row must
            // stay unbounded or every user above the final bound would be free.
            // It arrives with its "up to" FILLED IN — the next power-of-ten step
            // above the highest bound so far (999 → 9,999 → 99,999 ...) — so
            // pressing "Add a tier" several times gives a valid, ascending table
            // instead of a run of blank bands that the preview refuses.
            const next = [...list];
            const last = next[next.length - 1];
            const highest = next
                .slice(0, -1)
                .map((r) => Number(r.upTo))
                .filter((n) => Number.isInteger(n) && n > 0)
                .reduce((m, n) => Math.max(m, n), 0);
            const upTo = String(highest > 0 ? (highest + 1) * 10 - 1 : 999);
            next.splice(next.length - 1, 0, { upTo, rate: last ? last.rate : '1' });
            return next;
        });

    const removeRow = (index) =>
        setRows((list) => {
            if (list.length <= 1) return list;
            const next = list.filter((_, i) => i !== index);
            // Removing the open-ended band makes the one above it the last: it must
            // become open-ended too, or a bound the screen no longer shows ("no
            // limit") would still be published.
            if (index === list.length - 1) next[next.length - 1] = { ...next[next.length - 1], upTo: '' };
            return next;
        });

    const resetToDefaults = () => {
        if (!meta?.defaults) return;
        seedFrom(meta.defaults);
    };

    /* ------------------------------------------------------------------ *
     * Publish
     * ------------------------------------------------------------------ */
    const publish = async () => {
        if (formProblem) return; // every problem is shown beside its field
        setSaving(true);
        setError('');
        try {
            const tiers = rows.map((r, i) => ({
                upTo: i === rows.length - 1 && r.upTo === '' ? null : Number(r.upTo),
                rate: Number(r.rate),
            }));
            const res = await axios.put(
                `${BASE_URL}/billing/plan`,
                {
                    tiers,
                    billableMode: mode,
                    inactiveLookbackMonths: Number(lookback),
                    freeOnboardingMonths: Number(freeMonths),
                    effectiveFrom,
                    note: note || undefined,
                },
                { headers }
            );
            if (onFlash) onFlash(res?.data?.message || 'Published');
            setNote('');
            await load();
            if (showHistory) loadHistory();
            if (companies) loadCompanies();
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not publish the pricing.');
        } finally {
            setSaving(false);
        }
    };

    const loadHistory = useCallback(async () => {
        try {
            const res = await axios.get(`${BASE_URL}/billing/plan/history`, { headers });
            setHistory(res?.data?.data || []);
        } catch (err) {
            setHistory([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const loadCompanies = useCallback(async () => {
        setCompaniesLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/billing/companies`, { headers });
            setCompanies(res?.data?.data || null);
        } catch (err) {
            setCompanies(null);
            setError(err?.response?.data?.message || 'Could not calculate the companies.');
        } finally {
            setCompaniesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    /**
     * Opens one company's terms for editing.
     *
     * An OVERRIDDEN value is seeded into the box; an inherited one is left BLANK
     * with the inherited figure shown as the placeholder. That is the only way
     * the screen can express "this company follows the plan" distinctly from
     * "this company happens to be set to the same number as the plan" -- and the
     * difference decides what happens when the plan changes.
     */
    const openTerms = (c) => {
        setEditing(c.companyId);
        setEditError('');
        setEditDraft({
            connectionDate:
                c.connectionDateSource === 'override' && c.connectionDate
                    ? new Date(c.connectionDate).toISOString().slice(0, 10)
                    : '',
            freeOnboardingMonths:
                c.freeOnboardingMonthsSource === 'override'
                    ? String(c.freeOnboardingMonths)
                    : '',
            note: c.termsNote || '',
        });
    };

    const saveTerms = async (c) => {
        if (Object.keys(termErrors).length) return; // the form shows why
        setEditSaving(true);
        setEditError('');
        try {
            // An empty box means "follow the plan", which the API spells null.
            // Sending 0 instead would silently bill the connection month.
            const res = await axios.put(
                `${BASE_URL}/billing/company/${c.companyId}/terms`,
                {
                    connectionDate: editDraft.connectionDate || null,
                    freeOnboardingMonths:
                        editDraft.freeOnboardingMonths === '' ? null : Number(editDraft.freeOnboardingMonths),
                    note: editDraft.note || '',
                },
                { headers }
            );
            if (onFlash) onFlash(res?.data?.message || 'Terms saved');
            setEditing(null);
            loadCompanies();
        } catch (err) {
            setEditError(err?.response?.data?.message || 'Could not save the terms.');
        } finally {
            setEditSaving(false);
        }
    };

    // One company's terms, checked as typed (the server applies the same rules):
    // a connection date in the future would leave the company never billed.
    const termErrors = (() => {
        const e = {};
        const d = editDraft.connectionDate;
        if (d) {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(new Date(`${d}T00:00:00`).getTime())) e.connectionDate = 'Enter a real date';
            else if (d > todayIso()) e.connectionDate = 'Cannot be in the future — the company would never be billed';
            else if (Number(d.slice(0, 4)) < 2000) e.connectionDate = 'Check the year';
        }
        const m = String(editDraft.freeOnboardingMonths).trim();
        if (m !== '') {
            if (!/^\d+$/.test(m)) e.freeOnboardingMonths = 'Use a whole number of months';
            else if (Number(m) < limits.minFreeOnboardingMonths || Number(m) > limits.maxFreeOnboardingMonths) {
                e.freeOnboardingMonths = `Between ${limits.minFreeOnboardingMonths} and ${limits.maxFreeOnboardingMonths}, or empty for the default`;
            }
        }
        if ((editDraft.note || '').length > 200) e.note = 'Keep the note to 200 characters';
        return e;
    })();
    const termsInvalid = Object.keys(termErrors).length > 0;

    if (loading) return <Skeleton count={3} height={90} />;

    const modeLabel = (key) => (modes.find((m) => m.key === key) || {}).label || key;

    /**
     * The footer each editable card carries.
     *
     * Says what Save will do, not just that it will do something: an admin who
     * presses Save here and sees the page still reporting the old rates would
     * reasonably think it failed, when in fact the version is dated and waiting.
     * `dirty` is this card's own state; the write covers both cards.
     */
    const saveBar = (dirty) => (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-0 border-t border-solid border-gray-200 pt-4">
            <p className="min-w-0 flex-1 text-sm text-gray-500">
                {tierProblem || previewError
                    ? <span style={{ color: '#b91c1c' }}>Fix the tier table before saving.</span>
                    : formProblem
                        ? <span style={{ color: '#b91c1c' }}>Fix the fields marked in red on this page before saving.</span>
                    : dirty
                        ? <>Saves as a new pricing version, taking effect <strong>{shortDate(effectiveFrom)}</strong>. Invoices already finalised keep their own rates.</>
                        : 'No unsaved changes.'}
            </p>
            <div className="flex items-center gap-2">
                {dirty && !previewError && !formProblem && (
                    <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
                        style={{ backgroundColor: '#fffbeb', color: '#b45309' }}
                    >
                        Unsaved changes
                    </span>
                )}
                {/* border-0 appearance-none spelled out: this project loads only
                    @tailwind utilities, with no preflight, so a bare <button>
                    otherwise keeps Chrome's default button chrome. */}
                <button
                    type="button"
                    disabled={saving || !dirty || Boolean(previewError) || formProblem}
                    onClick={publish}
                    className={`rounded-md border-0 appearance-none px-4 py-2 text-sm font-medium text-white ${
                        saving || !dirty || previewError || formProblem ? 'cursor-default opacity-50' : 'cursor-pointer'
                    }`}
                    style={{ backgroundColor: BRAND }}
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );

    return (
        <div>
            {error && (
                <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* ---------------------------------------------------------- *
                What is in force
                ---------------------------------------------------------- */}
            <div className="bg-white rounded-lg p-5">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">Corporate pricing</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            How companies are charged for the people they put on the platform. This is
                            separate from individual membership, which is billed through Stripe and is
                            not affected by anything on this page.
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <span
                            className="rounded-full px-2.5 py-1 text-[11px] font-medium whitespace-nowrap"
                            style={
                                current?.isDefault
                                    ? { backgroundColor: '#fffbeb', color: '#b45309' }
                                    : { backgroundColor: '#ecfdf5', color: '#047857' }
                            }
                        >
                            {current?.isDefault
                                ? 'Using the built-in rates'
                                : `Version ${current?.version} · since ${shortDate(current?.effectiveFrom)}`}
                        </span>
                        {scheduled && (
                            <span
                                className="rounded-full px-2.5 py-1 text-[11px] font-medium whitespace-nowrap"
                                style={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }}
                            >
                                Version {scheduled.version} starts {shortDate(scheduled.effectiveFrom)}
                            </span>
                        )}
                    </div>
                </div>

                {/* The single most important thing a reader needs to know before
                    they touch the table below. */}
                <div
                    className="mb-5 rounded p-3 text-sm"
                    style={{ backgroundColor: '#FFF5F3', color: '#7c2d12' }}
                >
                    <strong>Pricing is progressive.</strong> Each band is charged at its own rate, so a
                    company with 5,000 people pays the first-band rate on the first band and the
                    second-band rate on the rest &mdash; not the second-band rate on everyone. The
                    preview below shows exactly what any head count comes to.
                </div>

                {/* ---------------------------------------------------------- *
                    The tier table
                    ---------------------------------------------------------- */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">Tiers</h4>
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        disabled={!meta?.defaults}
                        title="Put the table back to the rates agreed with the client. Nothing is saved until you press Save."
                        style={BTN.outline}
                        {...hoverBg('#f3f4f6', '#fff')}
                    >
                        <ResetIcon /> Reset to the agreed table
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 640 }}>
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                {/* Fixed widths: a message under a field must not shift the columns sideways. */}
                                <th className="py-2 pr-3 font-medium" style={{ width: '8%' }}>Tier</th>
                                <th className="py-2 pr-3 font-medium" style={{ width: '26%' }}>Users from</th>
                                <th className="py-2 pr-3 font-medium" style={{ width: '26%' }}>Up to</th>
                                <th className="py-2 pr-3 font-medium" style={{ width: '32%' }}>Rate per user</th>
                                <th className="py-2" style={{ width: '8%' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => {
                                const last = i === rows.length - 1;
                                // A band starts one above the previous band's limit; the
                                // first always at 1. Editing a start moves that limit.
                                const prevUpTo = i === 0 ? '' : String(rows[i - 1].upTo).trim();
                                const fromValue = i === 0 ? '1' : prevUpTo === '' ? '' : isWhole(prevUpTo) ? String(Number(prevUpTo) + 1) : prevUpTo;
                                const err = tierErrors[i] || {};
                                const fErr = fromError(i);
                                const bad = (m) => (m ? { borderColor: '#dc2626', outlineColor: '#dc2626' } : undefined);
                                const msg = (m) => (m ? <div className="mt-1 text-xs" style={{ color: '#b91c1c', maxWidth: 220 }}>{m}</div> : null);
                                return (
                                    <tr key={i} className="border-0 border-t border-solid border-gray-200 align-top">
                                        <td className="py-2 pr-3 text-gray-500">{i + 1}</td>
                                        <td className="py-2 pr-3">
                                            {i === 0 ? (
                                                <input
                                                    type="number"
                                                    value="1"
                                                    disabled
                                                    title="The first band always starts at 1, so every user is priced."
                                                    className="w-28 px-2 py-1 text-sm text-gray-500 text-right"
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    min="2"
                                                    step="1"
                                                    value={fromValue}
                                                    onChange={(e) => setFrom(i, e.target.value)}
                                                    title={`Where tier ${i + 1} starts. Changing it moves where tier ${i} ends.`}
                                                    className="w-28 px-2 py-1 text-sm text-gray-900 text-right"
                                                    style={bad(fErr)}
                                                    aria-invalid={Boolean(fErr)}
                                                />
                                            )}
                                            {msg(fErr)}
                                        </td>
                                        <td className="py-2 pr-3">
                                            {last ? (
                                                <span className="text-gray-500 inline-block py-1">
                                                    no limit
                                                </span>
                                            ) : (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={row.upTo}
                                                    onChange={(e) => { setLastEdit({ i, field: 'upTo' }); setRow(i, 'upTo', e.target.value); }}
                                                    className="w-28 px-2 py-1 text-sm text-gray-900 text-right"
                                                    style={bad(err.upTo)}
                                                    aria-invalid={Boolean(err.upTo)}
                                                />
                                            )}
                                            {msg(err.upTo)}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <span className="text-gray-500 mr-1">$</span>
                                            <input
                                                type="number"
                                                min="0.01"
                                                max={limits.maxRate}
                                                step="0.01"
                                                value={row.rate}
                                                onChange={(e) => setRow(i, 'rate', e.target.value)}
                                                className="w-24 px-2 py-1 text-sm text-gray-900 text-right"
                                                style={bad(err.rate)}
                                                aria-invalid={Boolean(err.rate)}
                                            />
                                            {msg(err.rate)}
                                        </td>
                                        <td className="py-2 text-right">
                                            {rows.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(i)}
                                                    title={`Remove tier ${i + 1}`}
                                                    aria-label={`Remove tier ${i + 1}`}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        width: 32, height: 32, borderRadius: 8, border: '1px solid #fecaca',
                                                        background: '#fef2f2', color: '#dc2626', cursor: 'pointer', padding: 0,
                                                    }}
                                                    {...hoverBg('#fee2e2', '#fef2f2')}
                                                >
                                                    <BinIcon />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                    {rows.length < limits.maxTiers ? (
                        <button
                            type="button"
                            onClick={addRow}
                            title="Adds a band above the open-ended last one, with its limit filled in — change it if you need to."
                            style={BTN.add}
                            {...hoverBg('#ffe9e3', '#FFF5F3')}
                        >
                            <PlusIcon /> Add a tier
                        </button>
                    ) : (
                        <span className="text-xs text-gray-500">
                            {limits.maxTiers} tiers is the most a plan can have — remove one to add another.
                        </span>
                    )}
                </div>

                {/* ---------------------------------------------------------- *
                    Preview
                    ---------------------------------------------------------- */}
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-900">What that costs</h4>
                    <p className="mt-1 mb-3 text-sm text-gray-500">
                        Priced by the same code that produces the invoices, so this is literally what a
                        company of that size would be charged.
                    </p>

                    {previewError ? (
                        <div
                            className="rounded p-3 text-sm"
                            style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}
                        >
                            {previewError}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                        <th className="py-2 pr-3 font-medium">Head count</th>
                                        <th className="py-2 pr-3 font-medium text-right">Monthly total</th>
                                        <th className="py-2 font-medium text-right">Blended rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.map((p) => (
                                        <tr
                                            key={p.users}
                                            className="border-0 border-t border-solid border-gray-200"
                                        >
                                            <td className="py-2 pr-3 text-gray-900">
                                                {p.users.toLocaleString()}
                                            </td>
                                            <td className="py-2 pr-3 text-right font-medium text-gray-900">
                                                {money(p.total)}
                                            </td>
                                            <td className="py-2 text-right text-gray-500">
                                                ${p.blendedRate} / user
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {saveBar(tiersDirty)}
            </div>

            {/* ---------------------------------------------------------- *
                Who is billed
                ---------------------------------------------------------- */}
            <div className="bg-white rounded-lg p-5 mt-6">
                <h3 className="text-base font-semibold text-gray-900">Who is billed</h3>
                <p className="mt-1 mb-4 text-sm text-gray-500">
                    Which of a company&rsquo;s people count toward its head count. This changes the
                    amount, so it is saved with the rates as part of the same version.
                </p>

                <div className="divide-y divide-gray-200 border-0 border-t border-solid border-gray-200">
                    {modes.map((m) => (
                        <label
                            key={m.key}
                            htmlFor={`mode-${m.key}`}
                            className="flex items-start gap-3 py-3 cursor-pointer"
                        >
                            <input
                                id={`mode-${m.key}`}
                                type="radio"
                                name="billableMode"
                                checked={mode === m.key}
                                onChange={() => setMode(m.key)}
                                className="mt-1 cursor-pointer"
                                style={{ accentColor: BRAND }}
                            />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium text-gray-900">{m.label}</span>
                                <span className="mt-1 block text-sm text-gray-500">{m.description}</span>
                            </span>
                        </label>
                    ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <label htmlFor="lookback" className="text-sm text-gray-900">
                        A member counts as inactive after
                    </label>
                    <input
                        id="lookback"
                        type="number"
                        min={limits.minLookbackMonths || 1}
                        max={limits.maxLookbackMonths || 24}
                        step="1"
                        value={lookback}
                        onChange={(e) => setLookback(e.target.value)}
                        className="w-16 px-2 py-1 text-sm text-gray-900 text-center"
                        style={badBorder(lookbackError)}
                        aria-invalid={Boolean(lookbackError)}
                    />
                    <span className="text-sm text-gray-900">
                        months with no recorded exercise, including the current month.
                    </span>
                </div>
                <FieldError>{lookbackError}</FieldError>
                <p className="mt-2 text-sm text-gray-500">
                    Exercise means minutes in a training zone from any connected device. Sedentary
                    &ldquo;out of range&rdquo; time does not count, the same as it does not count
                    toward a goal.
                    {mode !== 'active' && (
                        <> With the population selected above, this only affects the figures shown on
                        the invoice &mdash; it does not change the amount.</>
                    )}
                </p>

                {/* Lives in this card rather than beside the rates: it decides
                    WHICH MONTHS are billed, not what they cost. Versioned with
                    everything else, because an invoice has to be explicable once
                    a company's terms have been renegotiated. */}
                <div className="mt-5 border-0 border-t border-solid border-gray-200 pt-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <label htmlFor="freeMonths" className="text-sm text-gray-900">
                            A new company&rsquo;s first
                        </label>
                        <input
                            id="freeMonths"
                            type="number"
                            min={limits.minFreeOnboardingMonths ?? 0}
                            max={limits.maxFreeOnboardingMonths ?? 12}
                            step="1"
                            value={freeMonths}
                            onChange={(e) => setFreeMonths(e.target.value)}
                            className="w-16 px-2 py-1 text-sm text-gray-900 text-center"
                            style={badBorder(freeMonthsError)}
                            aria-invalid={Boolean(freeMonthsError)}
                        />
                        <span className="text-sm text-gray-900">
                            month{Number(freeMonths) === 1 ? '' : 's'} are free, counting from the month
                            it connects.
                        </span>
                    </div>
                    <FieldError>{freeMonthsError}</FieldError>
                    {!freeMonthsError && <p className="mt-2 text-sm text-gray-500">
                        {Number(freeMonths) === 0
                            ? 'A company is billed from its connection month, with no free period.'
                            : <>A company that connects in March is first billed for{' '}
                                {['March', 'April', 'May', 'June', 'July', 'August', 'September',
                                  'October', 'November', 'December', 'January', 'February', 'March'][
                                    Math.min(12, Math.max(0, Number(freeMonths) || 0))
                                ]}. The client&rsquo;s agreed rule is one free month.</>}
                        {' '}Individual companies can be put on different terms below.
                    </p>}
                </div>

                {saveBar(audienceDirty)}
            </div>

            {/* ---------------------------------------------------------- *
                Versioning — shared by both Save buttons above.

                No Save of its own: the date and note are not settings in their
                own right, they are how the NEXT save is recorded. A third button
                here would have published the same version as the other two and
                left an admin guessing which one was the real one.
                ---------------------------------------------------------- */}
            <div className="bg-white rounded-lg p-5 mt-6">
                <h3 className="text-base font-semibold text-gray-900">When changes take effect</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Applies to whichever card you save above. Saving creates a new pricing version
                    rather than editing the current one.
                    <strong> Invoices that have already been finalised keep the rates they were
                    calculated with</strong> and are not re-priced.
                </p>

                <div className="mt-4 flex flex-wrap items-end gap-4">
                    <div>
                        <label htmlFor="effectiveFrom" className="block text-sm font-medium text-gray-900">
                            Takes effect from
                        </label>
                        <input
                            id="effectiveFrom"
                            type="date"
                            value={effectiveFrom}
                            required
                            onChange={(e) => setEffectiveFrom(e.target.value)}
                            className="mt-1 px-2 py-1 text-sm text-gray-900"
                            style={badBorder(effectiveError)}
                            aria-invalid={Boolean(effectiveError)}
                        />
                        <FieldError>{effectiveError}</FieldError>
                    </div>
                    <div className="min-w-0 flex-1">
                        <label htmlFor="note" className="block text-sm font-medium text-gray-900">
                            Note <span className="font-normal text-gray-400">(optional · {note.length}/{NOTE_MAX})</span>
                        </label>
                        <input
                            id="note"
                            type="text"
                            value={note}
                            maxLength={NOTE_MAX}
                            placeholder="Why this changed"
                            onChange={(e) => setNote(e.target.value)}
                            className="mt-1 w-full px-2 py-1 text-sm text-gray-900"
                            style={badBorder(noteError)}
                        />
                        <FieldError>{noteError}</FieldError>
                    </div>
                </div>
                <FieldWarn>{effectiveWarning}</FieldWarn>

                <p className="mt-3 text-sm text-gray-500">
                    An invoice uses the version in force on the first day of the month it covers,
                    which is why this defaults to the start of next month: a date inside the current
                    month would re-price days that have already been lived.
                </p>

                <button
                    type="button"
                    onClick={() => {
                        setShowHistory((s) => !s);
                        if (!history.length) loadHistory();
                    }}
                    className="mt-4"
                    aria-expanded={showHistory}
                    style={BTN.outline}
                    {...hoverBg('#f3f4f6', '#fff')}
                >
                    <HistoryIcon /> {showHistory ? 'Hide version history' : 'Show version history'}
                    <span aria-hidden="true" style={{ fontSize: 10, marginLeft: 2 }}>{showHistory ? '▲' : '▼'}</span>
                </button>

                {showHistory && (
                    <div className="mt-3 overflow-x-auto">
                        {history.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                Nothing has been published yet &mdash; the built-in rates are in force.
                            </p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                        <th className="py-2 pr-3 font-medium">Version</th>
                                        <th className="py-2 pr-3 font-medium">From</th>
                                        <th className="py-2 pr-3 font-medium">Tiers</th>
                                        <th className="py-2 pr-3 font-medium">Billed on</th>
                                        <th className="py-2 font-medium">Published by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((h) => (
                                        <tr
                                            key={h.version}
                                            className="border-0 border-t border-solid border-gray-200"
                                        >
                                            <td className="py-2 pr-3 text-gray-900">{h.version}</td>
                                            <td className="py-2 pr-3 text-gray-500">
                                                {shortDate(h.effectiveFrom)}
                                            </td>
                                            <td className="py-2 pr-3 text-gray-500">
                                                {(h.tiers || [])
                                                    .map((t) => `$${t.rate}`)
                                                    .join(' / ')}
                                            </td>
                                            <td className="py-2 pr-3 text-gray-500">
                                                {modeLabel(h.billableMode)}
                                            </td>
                                            <td className="py-2 text-gray-500">
                                                {h.createdBy?.name || h.createdBy?.email || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* ---------------------------------------------------------- *
                The calculation
                ---------------------------------------------------------- */}
            <div className="bg-white rounded-lg p-5 mt-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">Companies this month</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            What each company comes to under the pricing currently in force.
                            <strong> Nothing is charged</strong> &mdash; these are figures to invoice
                            against, collected offline.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={loadCompanies}
                        disabled={companiesLoading}
                        className={`rounded-md border-0 appearance-none px-3 py-1.5 text-sm font-medium text-white ${
                            companiesLoading ? 'cursor-wait opacity-60' : 'cursor-pointer'
                        }`}
                        style={{ backgroundColor: BRAND }}
                    >
                        {companiesLoading ? 'Calculating…' : companies ? 'Recalculate' : 'Calculate'}
                    </button>
                </div>

                {/* The population is a commercial decision worth real money, so the
                    cost of each reading is shown side by side rather than left to be
                    discovered by publishing a version and recalculating. Only
                    companies past their onboarding month are counted, so these
                    agree with the table's own total. */}
                {companies && companies.companies.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-3">
                        {(companies.companies[0].alternatives || []).map((alt) => {
                            const total = companies.companies.reduce(
                                (sum, c) =>
                                    sum +
                                    (c.billableThisMonth
                                        ? (c.alternatives.find((a) => a.mode === alt.mode) || {}).total || 0
                                        : 0),
                                0
                            );
                            const live = alt.mode === companies.plan.billableMode;
                            return (
                                <div
                                    key={alt.mode}
                                    className="rounded px-3 py-2"
                                    style={
                                        live
                                            ? { backgroundColor: '#FFF5F3', border: `1px solid ${BRAND}` }
                                            : { backgroundColor: '#f9fafb' }
                                    }
                                >
                                    <span className="block text-xs text-gray-500">
                                        {modeLabel(alt.mode)}
                                        {live && ' · in force'}
                                    </span>
                                    <span className="block text-sm font-semibold text-gray-900">
                                        {money(total)} / month
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {companies && (
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                    <th className="py-2 pr-3 font-medium">Company</th>
                                    <th className="py-2 pr-3 font-medium">Billing from</th>
                                    <th className="py-2 pr-3 font-medium text-right">Accepted</th>
                                    <th className="py-2 pr-3 font-medium text-right">Active</th>
                                    <th className="py-2 pr-3 font-medium text-right">Inactive</th>
                                    <th className="py-2 pr-3 font-medium text-right">Invited</th>
                                    <th className="py-2 pr-3 font-medium text-right">Billed on</th>
                                    <th className="py-2 font-medium text-right">Monthly total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.companies.map((c) => (
                                    <tr
                                        key={c.companyId}
                                        className="border-0 border-t border-solid border-gray-200"
                                    >
                                        <td className="py-2 pr-3">
                                            <span className="block text-gray-900">{c.companyName || '—'}</span>
                                            {!c.billableThisMonth && (
                                                <span className="block text-xs" style={{ color: '#b45309' }}>
                                                    Onboarding &mdash; first billed {c.firstBillableMonth}
                                                </span>
                                            )}
                                            {!c.linkedCustomerRecord && (
                                                <span className="block text-xs text-gray-400">
                                                    No customer record, so outstanding invitations cannot be counted
                                                </span>
                                            )}
                                        </td>
                                        {/* Terms, and the control that changes them.
                                            Put beside the company rather than behind a
                                            separate screen: the question "why is this one
                                            not billing yet" is asked while looking at
                                            exactly this table. */}
                                        <td className="py-2 pr-3 align-top">
                                            <span className="block text-gray-900">{c.firstBillableMonth}</span>
                                            <span className="block text-[11px] text-gray-400">
                                                {c.freeOnboardingMonths === 0
                                                    ? 'no free period'
                                                    : `${c.freeOnboardingMonths} free month${c.freeOnboardingMonths === 1 ? '' : 's'}`}
                                                {c.hasCustomTerms && (
                                                    <span style={{ color: '#b45309' }}> &middot; custom</span>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => (editing === c.companyId ? setEditing(null) : openTerms(c))}
                                                className="mt-0.5 border-0 appearance-none bg-transparent p-0 text-[11px] font-medium cursor-pointer underline"
                                                style={{ color: '#0694F3' }}
                                            >
                                                {editing === c.companyId ? 'Close' : 'Edit terms'}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-3 text-right text-gray-500">
                                            {c.counts.accepted.toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-3 text-right text-gray-500">
                                            {c.counts.active.toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-3 text-right text-gray-500">
                                            {c.counts.inactive.toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-3 text-right text-gray-500">
                                            {c.counts.invited.toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-3 text-right font-medium text-gray-900">
                                            {c.billableUsers.toLocaleString()}
                                        </td>
                                        <td className="py-2 text-right font-medium text-gray-900">
                                            {c.billableThisMonth ? money(c.total) : '—'}
                                        </td>
                                    </tr>
                                ))}
                                {/* Rendered as a sibling row rather than inside the
                                    row above, so the table's columns are not pushed
                                    about by a form that is only sometimes there. */}
                                {companies.companies
                                    .filter((c) => c.companyId === editing)
                                    .map((c) => (
                                        <tr key={`${c.companyId}-edit`} className="border-0">
                                            <td colSpan={8} className="pb-4">
                                                <div className="rounded p-3" style={{ backgroundColor: '#f9fafb' }}>
                                                    <div className="mb-2 text-xs font-medium text-gray-900">
                                                        {c.companyName} &mdash; billing terms
                                                    </div>
                                                    {editError && (
                                                        <p className="mb-2 text-xs" style={{ color: '#b91c1c' }}>{editError}</p>
                                                    )}
                                                    <div className="flex flex-wrap items-end gap-4">
                                                        <div>
                                                            <label htmlFor={`cd-${c.companyId}`} className="block text-[11px] text-gray-500">
                                                                Connected on
                                                            </label>
                                                            <input
                                                                id={`cd-${c.companyId}`}
                                                                type="date"
                                                                value={editDraft.connectionDate}
                                                                max={todayIso()}
                                                                onChange={(e) => setEditDraft((d) => ({ ...d, connectionDate: e.target.value }))}
                                                                className="mt-1 px-2 py-1 text-sm text-gray-900"
                                                                style={badBorder(termErrors.connectionDate)}
                                                                aria-invalid={Boolean(termErrors.connectionDate)}
                                                            />
                                                            <FieldError>{termErrors.connectionDate}</FieldError>
                                                            <span className="mt-1 block text-[11px] text-gray-400">
                                                                {c.connectionDateSource === 'override'
                                                                    ? 'Set by hand'
                                                                    : `Empty = record created ${shortDate(c.connectionDate)}`}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <label htmlFor={`fm-${c.companyId}`} className="block text-[11px] text-gray-500">
                                                                Free months
                                                            </label>
                                                            <input
                                                                id={`fm-${c.companyId}`}
                                                                type="number"
                                                                min={limits.minFreeOnboardingMonths ?? 0}
                                                                max={limits.maxFreeOnboardingMonths ?? 12}
                                                                placeholder={String(companies.plan.freeOnboardingMonths ?? 1)}
                                                                value={editDraft.freeOnboardingMonths}
                                                                step="1"
                                                                onChange={(e) => setEditDraft((d) => ({ ...d, freeOnboardingMonths: e.target.value }))}
                                                                className="mt-1 w-20 px-2 py-1 text-sm text-gray-900 text-center"
                                                                style={badBorder(termErrors.freeOnboardingMonths)}
                                                                aria-invalid={Boolean(termErrors.freeOnboardingMonths)}
                                                            />
                                                            <FieldError>{termErrors.freeOnboardingMonths}</FieldError>
                                                            <span className="mt-1 block text-[11px] text-gray-400">
                                                                Empty = platform default ({companies.plan.freeOnboardingMonths ?? 1})
                                                            </span>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <label htmlFor={`nt-${c.companyId}`} className="block text-[11px] text-gray-500">
                                                                Note
                                                            </label>
                                                            <input
                                                                id={`nt-${c.companyId}`}
                                                                type="text"
                                                                maxLength={200}
                                                                placeholder="Why this customer is not on standard terms"
                                                                value={editDraft.note}
                                                                onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                                                                className="mt-1 w-full px-2 py-1 text-sm text-gray-900"
                                                                style={badBorder(termErrors.note)}
                                                            />
                                                            <FieldError>{termErrors.note}</FieldError>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={editSaving || termsInvalid}
                                                            onClick={() => saveTerms(c)}
                                                            title={termsInvalid ? 'Fix the fields marked in red first' : undefined}
                                                            className={`rounded-md border-0 appearance-none px-3 py-1.5 text-sm font-medium text-white ${
                                                                editSaving || termsInvalid ? 'cursor-default opacity-50' : 'cursor-pointer'
                                                            }`}
                                                            style={{ backgroundColor: BRAND }}
                                                        >
                                                            {editSaving ? 'Saving\u2026' : 'Save terms'}
                                                        </button>
                                                    </div>
                                                    <p className="mt-2 text-[11px] text-gray-400">
                                                        Leaving a box empty puts this company back on the standard plan.
                                                        That is not the same as entering 0, which bills them from their
                                                        connection month.
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-0 border-t-2 border-solid border-gray-300">
                                    <td className="py-2 pr-3 font-medium text-gray-900" colSpan={6}>
                                        {companies.totals.companies} companies &middot; billed on{' '}
                                        {modeLabel(companies.plan.billableMode).toLowerCase()}
                                    </td>
                                    <td className="py-2 pr-3 text-right font-medium text-gray-900">
                                        {companies.totals.billableUsers.toLocaleString()}
                                    </td>
                                    <td className="py-2 text-right font-semibold text-gray-900">
                                        {money(companies.totals.total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                        <p className="mt-3 text-xs text-gray-400">
                            Head counts exclude each company&rsquo;s own administrator login, which is
                            how they manage their roster rather than a seat on the programme.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CorporateBilling;
